/**
 * frontend/src/utils/ui.ts
 * =========================
 * Piezas de interfaz reutilizables.
 *
 * Lo que vive acá se usa en más de una vista. Un componente duplicado entre
 * pantallas termina divergiendo —una versión escapa el HTML, la otra no; una
 * es accesible, la otra no— y el usuario percibe dos productos distintos.
 */

import { escapeHtml } from "./texto";
import { icon } from "./icons";

/** Keep keyboard focus inside the top dialog and release listeners when removed. */
export function prepararDialogo(overlay: HTMLElement, cerrar?: () => void): void {
  const anterior = document.activeElement instanceof HTMLElement && document.activeElement !== document.body
    ? document.activeElement : null;
  const controller = new AbortController();
  const observer = new MutationObserver(() => {
    if (overlay.isConnected) return;
    controller.abort();
    observer.disconnect();
    const abiertos = document.querySelectorAll(".modal-overlay, .wizard-overlay");
    const superior = abiertos[abiertos.length - 1];
    // Closing a manager may immediately open a wizard. Its cleanup must not
    // pull focus out of the newly opened dialog.
    if (anterior?.isConnected && (!superior || superior.contains(anterior))) anterior.focus();
  });
  observer.observe(document.body, { childList: true });
  const enfocables = () => Array.from(overlay.querySelectorAll<HTMLElement>(
    'button:not(:disabled), input:not(:disabled):not([type="hidden"]), select:not(:disabled), a[href], [tabindex="0"]',
  )).filter(el => el.getClientRects().length > 0);
  document.addEventListener("keydown", e => {
    const abiertos = document.querySelectorAll(".modal-overlay, .wizard-overlay");
    if (abiertos[abiertos.length - 1] !== overlay) return;
    if (e.key === "Escape" && cerrar) { e.preventDefault(); cerrar(); }
    if (e.key !== "Tab") return;
    const items = enfocables();
    const primero = items[0];
    const ultimo = items[items.length - 1];
    if (!primero) { e.preventDefault(); return; }
    if (e.shiftKey && (document.activeElement === primero || !overlay.contains(document.activeElement))) {
      e.preventDefault(); ultimo.focus();
    } else if (!e.shiftKey && (document.activeElement === ultimo || !overlay.contains(document.activeElement))) {
      e.preventDefault(); primero.focus();
    }
  }, { signal: controller.signal });
  requestAnimationFrame(() => { if (overlay.isConnected) enfocables()[0]?.focus(); });
}

/**
 * Ícono de ayuda con explicación al pasar el mouse.
 *
 * Los formularios de configuración tienen muchos controles y un párrafo debajo
 * de cada uno los vuelve ilegibles: el ruido tapa justamente los campos que
 * importan. La explicación completa vive en el tooltip y a la vista queda solo
 * la etiqueta.
 *
 * Accesible por teclado (`tabindex`) y para lectores de pantalla (`aria-label`
 * con el texto plano): si la única forma de leer la ayuda fuera el mouse, para
 * una parte de los usuarios no existiría.
 *
 * @param html   contenido del tooltip; admite <b> y <br> para estructurarlo.
 * @param plano  misma explicación sin marcas, para el lector de pantalla.
 *               Si se omite se deriva quitando las etiquetas.
 */
export function iconoAyuda(html: string, plano?: string): string {
  const texto = plano ?? html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

  return `<span class="info-tip" tabindex="0" role="note" aria-label="${escapeHtml(texto)}"
    >${icon("info")}<span class="info-tip__contenido" role="tooltip">${html}</span></span>`;
}


// ── Posicionamiento de tooltips ───────────────────────────────────────────────

/** Separación entre el ícono y el tooltip, y margen mínimo contra la ventana. */
const _MARGEN_TOOLTIP = 8;

/** El tooltip visible. Uno solo para toda la app: nunca hay dos a la vez. */
let _flotante: HTMLElement | null = null;
let _instalado = false;

/**
 * Hace que los tooltips de `iconoAyuda()` no los recorte ni los corra nada.
 *
 * El tooltip se posicionaba con CSS, siempre arriba del ícono y anidado dentro
 * de él. Dentro del wizard eso fallaba de dos formas:
 *
 *   1. `.wizard-modal__body` tiene `overflow-y: auto`, así que recortaba el
 *      tooltip contra el borde del modal. Con el ícono cerca del encabezado,
 *      media explicación quedaba tapada y no había manera de leerla.
 *   2. `.wizard-modal` se anima con `transform`. Un ancestro con transform crea
 *      bloque contenedor, y entonces ni `position: fixed` alcanza: el tooltip
 *      se posiciona contra el modal en vez de contra la ventana.
 *
 * Por eso lo que se muestra no es el nodo anidado sino una capa colgada de
 * `<body>`, donde ningún ancestro puede recortarla ni anclarla. El nodo del
 * marcado queda como portador del texto para que `iconoAyuda()` siga devolviendo
 * una sola cadena de HTML.
 *
 * Decidir si el tooltip entra arriba o hay que ponerlo abajo exige medir, y
 * medir es JavaScript: por eso la posición no puede quedar en el CSS.
 *
 * Un único listener delegado en `document`, no uno por ícono: los tooltips se
 * crean y se destruyen en cada render del wizard y los listeners quedarían
 * colgados.
 */
export function instalarTooltips(): void {
  if (_instalado) return;
  _instalado = true;

  // Dos portadores de ayuda: el ícono `iconoAyuda()` y cualquier elemento con
  // `data-tooltip`. El segundo existe para explicar por qué un control está
  // bloqueado, ahí mismo donde está el control — un aviso al pie del
  // formulario obliga a ir a buscarlo, y el usuario mira el botón, no el pie.
  const iconoDe = (target: EventTarget | null): HTMLElement | null =>
    target instanceof Element
      ? target.closest<HTMLElement>(".info-tip, [data-tooltip]")
      : null;

  // Al entrar en algo que NO lleva ayuda también se esconde. "Verificar" y
  // "Continuar" están pegados: pasando de uno bloqueado a otro que ya no lo
  // está, el motivo del primero quedaba flotando sobre un botón que funciona.
  document.addEventListener("mouseover", (e) => {
    const icono = iconoDe(e.target);
    if (icono) _mostrarTooltip(icono);
    else _ocultarTooltip();
  });
  document.addEventListener("mouseout", (e) => {
    if (iconoDe(e.target)) _ocultarTooltip();
  });

  // Teclado: el ícono es focusable, así que la ayuda no depende del mouse.
  document.addEventListener("focusin", (e) => {
    const icono = iconoDe(e.target);
    if (icono) _mostrarTooltip(icono);
  });
  document.addEventListener("focusout", (e) => {
    if (iconoDe(e.target)) _ocultarTooltip();
  });

  // El tooltip es `fixed` y el ícono no: al hacer scroll se separarían. Se
  // esconde en vez de perseguirlo — si el usuario está scrolleando, ya no lo
  // está leyendo.
  window.addEventListener("scroll", _ocultarTooltip, true);
  window.addEventListener("resize", _ocultarTooltip);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") _ocultarTooltip();
  });
}

function _capaTooltip(): HTMLElement {
  if (!_flotante) {
    _flotante = document.createElement("div");
    _flotante.className = "info-tip__flotante";
    _flotante.setAttribute("role", "tooltip");
    document.body.appendChild(_flotante);
  }
  return _flotante;
}

function _mostrarTooltip(icono: HTMLElement): void {
  const capa = _capaTooltip();

  const texto = icono.dataset.tooltip;
  if (texto !== undefined) {
    // `textContent`: lo de `data-tooltip` es texto del usuario o del backend y
    // no se interpreta como marcado.
    //
    // Sin nada que mostrar se esconde, no se sale sin hacer nada: "Verificar" y
    // "Continuar" están pegados, y al pasar de uno bloqueado a uno que ya no lo
    // está quedaba en pantalla el motivo del anterior, apuntando a un botón que
    // sí funciona.
    if (!texto) {
      _ocultarTooltip();
      return;
    }
    capa.textContent = texto;
  } else {
    const origen = icono.querySelector<HTMLElement>(".info-tip__contenido");
    if (!origen) {
      _ocultarTooltip();
      return;
    }
    // innerHTML del propio marcado, que `iconoAyuda()` ya escapó al construirlo.
    capa.innerHTML = origen.innerHTML;
  }

  capa.classList.add("info-tip__flotante--visible");
  _posicionarTooltip(icono, capa);
}

function _ocultarTooltip(): void {
  _flotante?.classList.remove("info-tip__flotante--visible");
}

function _posicionarTooltip(icono: HTMLElement, capa: HTMLElement): void {
  // Se limpia el volteo anterior antes de medir: si quedó abajo de la vez
  // pasada, la medición saldría del tamaño que tenía en el otro lado.
  capa.classList.remove("info-tip__flotante--abajo");

  const i = icono.getBoundingClientRect();
  const t = capa.getBoundingClientRect();
  const m = _MARGEN_TOOLTIP;
  const alto = window.innerHeight;

  // Arriba es la posición preferida: no tapa el campo que viene después. Se
  // baja solo si no entra, y si tampoco entra abajo gana el lado con más aire
  // — un tooltip apretado se lee; uno fuera de la ventana, no.
  const entraArriba = i.top - t.height - m >= m;
  const entraAbajo = i.bottom + t.height + m <= alto - m;
  const abajo = !entraArriba && (entraAbajo || alto - i.bottom > i.top);
  if (abajo) capa.classList.add("info-tip__flotante--abajo");

  // El clamp final es la red: con un tooltip más alto que la ventana ningún
  // lado entra, y sin esto quedaría cortado arriba o abajo.
  const top = Math.max(
    m,
    Math.min(abajo ? i.bottom + m : i.top - t.height - m, alto - t.height - m),
  );

  // Centrado en el ícono, pero sin salirse de la ventana.
  const centrado = i.left + i.width / 2 - t.width / 2;
  const left = Math.max(m, Math.min(centrado, window.innerWidth - t.width - m));

  capa.style.top = `${Math.round(top)}px`;
  capa.style.left = `${Math.round(left)}px`;
  capa.style.setProperty(
    "--info-tip-flecha",
    `${Math.round(i.left + i.width / 2 - left)}px`,
  );
}
